/* eslint-disable no-param-reassign */
import { BLOCK_NODE, FIELD_NODE } from '../constants/formNode';
import {
  isColliding,
  sortByPositionFactory,
  isWithin,
} from '../components/organisms/Grid/GridHelpers';
import { get, del } from 'object-path';
import { DATA_TYPE_LIST } from '../constants/typology/dataType';
import {
  ADVANCEDSELECT,
  GPSCOORDINATES,
  LATITUDE,
  LONGITUDE,
  MCQ,
  NUMBER,
} from '../constants/typology/fieldType';
import { isObject } from './compare';
import clone from './clone';

// Traverses children and marks the first visible field (not hidden, not data_)
// with isFocusOnInit: true. pathPrefix is the path of the parent container in form values.
export function addFocusOnFirstField(elements, hiddenBoxes = [], pathPrefix = '') {
  let found = false;

  function traverse(elems, prefix) {
    if (found) return elems;

    return elems.map((element) => {
      if (found) return element;

      const path = prefix ? `${prefix}.${element.id}` : element.id;

      if (element.type && !element.type.startsWith('data_')) {
        if (!hiddenBoxes.includes(path)) {
          found = true;
          return { ...element, isFocusOnInit: true };
        }
        return element;
      }

      if (hiddenBoxes.includes(path)) return element;

      // Non-hidden block: do not recurse into children since the node object is shared
      // across all rows in fields.map. Delegate to ContributeFormNodeBlockFields
      // via focusFirstRow so focus is only applied to row 0.
      if (Array.isArray(element.children)) {
        found = true;
        return { ...element, focusFirstRow: true };
      }

      return element;
    });
  }

  return traverse(elements, pathPrefix);
}

/**
 * Compute heights for all nodes in the form tree.
 *
 * @param {Object} formTree - The form structure
 * @param {Array} hiddenBoxes - Array of hidden element paths
 * @param {Object} values - Form values
 * @returns {Map} Map of path -> { height, y, childrenHeights, childrenBoxes }
 */
export function computeComputedHeights(formTree, hiddenBoxes, values) {
  if (!values) {
    return new Map();
  }
  // Map containing BLOCKS data
  // Structure: path -> { height, y, folded, childrenHeights, childrenBoxes }
  const blocks = new Map();
  const hiddenSet = new Set(hiddenBoxes);

  // Processes one row of children: computes heights, applies reflow,
  // updates Y positions of child blocks in the Map.
  // useDefinedY=true: root keeps designer-defined positions (box.y for all children).
  // useDefinedY=false (default): child blocks start at y=0 (dynamic heights,
  // reflow handles placement).
  function processRow(children, itemValues, makeChildPath, useDefinedY = false) {
    const childBoxes = [];
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const childHeight = computeNodeHeight(child, makeChildPath(child), itemValues);
      const y = (!useDefinedY && child.nodeType === BLOCK_NODE) ? 0 : child.box.y;
      childBoxes.push({ ...child.box, y, height: childHeight });
    }

    const reflowedBoxes = reflow(childBoxes);

    for (let i = 0; i < children.length; i++) {
      if (children[i].nodeType === BLOCK_NODE) {
        const existing = blocks.get(makeChildPath(children[i]));
        if (existing) existing.y = reflowedBoxes[i].y;
      }
    }

    const rowHeight = reflowedBoxes.reduce(
      (max, box) => (box.height !== 0 ? Math.max(max, box.y + box.height) : max),
      0,
    );

    return { reflowedBoxes, rowHeight };
  }

  // Recursive function to compute height of a node
  // Returns the height of the node
  function computeNodeHeight(node, path, parentValues) {
    if (hiddenSet.has(path)) {
      if (node.nodeType === BLOCK_NODE || node.nodeType === 'root') {
        blocks.set(
          path,
          {
            height: 0,
            y: 0,
            folded: false,
            childrenHeights: {},
            childrenBoxes: {},
          });
      }
      return 0;
    }

    if (node.nodeType === FIELD_NODE) {
      return node.box.height;
    }

    if (node.nodeType === BLOCK_NODE) {
      const isFolded = parentValues?.[node.id]?.folded ?? false;

      if (isFolded) {
        blocks.set(path, { height: 1, y: 0, folded: true, childrenHeights: {}, childrenBoxes: {} });
        return 1;
      }

      const blockValues = get(parentValues, `${node.id}.$items`, null);

      if (!blockValues || blockValues.length === 0) {
        if (node.duplicable) {
          blocks.set(
            path,
            {
              height: 2,
              y: 0,
              folded: false,
              childrenHeights: {},
              childrenBoxes: {},
            });
          return 2;
        }
      }

      const actualBlockValues = blockValues && blockValues.length > 0 ? blockValues : [{}];
      let totalHeight = 1; // Header row
      const childrenHeights = {};
      const childrenBoxes = {};

      for (let itemIndex = 0; itemIndex < actualBlockValues.length; itemIndex++) {
        const itemPath = `${path}.$items[${itemIndex}]`;
        const { reflowedBoxes, rowHeight } = processRow(
          node.children,
          actualBlockValues[itemIndex] || {},
          (child) => `${itemPath}.${child.id}`,
        );
        childrenBoxes[itemIndex] = reflowedBoxes;
        childrenHeights[itemIndex] = rowHeight;
        totalHeight += rowHeight;
      }

      if (node.duplicable) totalHeight += 1;

      blocks.set(
        path,
        {
          height: totalHeight,
          y: 0,
          folded: false,
          childrenHeights,
          childrenBoxes,
        });
      return totalHeight;
    }

    if (node.nodeType === 'root') {
      const { reflowedBoxes, rowHeight: totalHeight } = processRow(
        node.children,
        values,
        (child) => child.id,
        true,
      );

      const childrenHeights = {};
      node.children.forEach((child, i) => { childrenHeights[i] = reflowedBoxes[i].height; });

      blocks.set(
        'root',
        {
          height: totalHeight,
          y: 0,
          folded: false,
          childrenHeights,
          childrenBoxes: { 0: reflowedBoxes },
        });
      return totalHeight;
    }

    return 0;
  }

  computeNodeHeight(formTree, 'root', values);

  return blocks;
}

function selectRemoteValueToApi(tuple) {
  if (!tuple) {
    return null;
  }
  const { value, label } = tuple;
  if (value && value.value) return { id: value.value, label };
  return { id: value, label };
}
function selectRemoteValueFromApi(tuple) {
  if (!tuple) {
    return null;
  }
  const { id, label } = tuple;
  return { value: id, label };
}

//get all values of fields/data by id in row
function getAnswerValues({ data, fields } = {}) {
  const allFields = (data || []).concat(fields || []);
  const values = {};

  // Cache constants
  const latitudeType = `coordinates_${LATITUDE}`;
  const longitudeType = `coordinates_${LONGITUDE}`;
  const latitudeSuffix = `_${LATITUDE}`;
  const longitudeSuffix = `_${LONGITUDE}`;

  for (let i = 0, len = allFields.length; i < len; i++) {
    const { id: fieldId, value, items, itemApi, itemsApi, type } = allFields[i];
    let id = fieldId;

    if (type === latitudeType || type === longitudeType) {
      id = fieldId.replace(latitudeSuffix, '').replace(longitudeSuffix, '');
      if (!values[id]) values[id] = {};

      if (type === latitudeType) {
        values[id][LATITUDE] = value;
      } else {
        values[id][LONGITUDE] = value;
      }
      continue;
    }

    if (itemApi && itemApi.id === '') {
      itemApi.id = null;
    }

    if (!values[id]) {
      values[id] = value ||
                   items ||
                   (itemApi?.id !== null ? selectRemoteValueFromApi(itemApi) : null) ||
                   (itemsApi ? itemsApi.map(selectRemoteValueFromApi).filter(Boolean) : null);
    }
  }

  return values;
}

//get all values of fields/data by id in rows and build Tree
function getAnswerRowsValues(rows = []) {
  return rows
    .filter((row) => !row.isHidden)
    .map((row) => ({
      ...getAnswerValues(row),
      ...(row?.blocks && buildAnswerTree(row.blocks)),
    }));
}

// Build the initial value tree a response tree
function buildAnswerTree(answerTree) {
  if (!answerTree?.length) return null;

  const initialValue = {};

  for (let i = 0, len = answerTree.length; i < len; i++) {
    const childAnswer = answerTree[i];

    if (!childAnswer?.id) continue;

    const values = getAnswerValues(childAnswer);

    if (!initialValue[childAnswer.id]) {
      initialValue[childAnswer.id] = {};
    }

    const currentChild = initialValue[childAnswer.id];
    Object.assign(currentChild, values);

    if (childAnswer.rows) {
      currentChild.$items = getAnswerRowsValues(childAnswer.rows);
    }

    if (childAnswer.blocks) {
      const tree = buildAnswerTree(childAnswer.blocks);
      currentChild.$items = [{ ...values, ...tree }];
    }
  }

  return initialValue;
}

export function buildAnswer(answer = {}) {
  return {
    ...getAnswerValues(answer),
    ...buildAnswerTree(answer.blocks),
  };
}
// Get initial values for a single node
export function getNodeInitialValues({ children }, initialValues = {}) {
  for (const child of children) {
    if (child.nodeType === BLOCK_NODE) {
      // Set initial values for each BLOCK_NODE child
      initialValues[child.id] = {
        $items: [getNodeInitialValues(child)],
      };
    }
  }
  return initialValues;
}

export function buildAnswers(answers = {}) {
  if (!isObject(answers)) return null;

  const keys = Object.keys(answers);
  if (!keys.length) return null;

  return keys.map((key) => {
    return buildAnswer(answers[key]);
  });
}

export function buildBlockAnswerChoices(answer) {
  const blocks = {};
  for (let i = 0; answer.blocks && i < answer.blocks.length; i++) {
    const block = answer.blocks[i];
    if (block.rows) {
      if (!block.rows.length) {
        continue;
      }

      let nbFields = block.rows.map((row) => {
        return (row.fields || []).length;
      });
      // check for subBlock answer Values
      block.rows.map((row) => {
        if (row.blocks && row.blocks.length > 0) {
          row.blocks.map((elem) => {
            blocks[elem.id] = {
              nbFields: (elem.rows || [])
                .map((e) => {
                  return (e.fields || []).length;
                })
                .reduce(function (a, b) {
                  return a + b;
                }, 0),
              confidence: row.color,
              rows: (elem.rows || []).map((e) => ({
                value: {
                  ...buildAnswer({
                    fields: e.fields,
                    blocks: (e.blocks || []).map((blockRow) => ({
                      ...blockRow,
                      ...(blockRow.rows ? blockRow.rows[0] : {}),
                    })),
                  }),
                },
                confidence: e.color,
                label: e.primaryKey,
              })),
            };
          });
        }
      });

      blocks[block.id] = {
        nbFields: nbFields.reduce(function (a, b) {
          return a + b;
        }, 0),
        confidence: block.color,
        rows: block.rows.map((row) => ({
          value: {
            ...buildAnswer({
              fields: row.fields,
              blocks: (row.blocks || []).map((blockRow) => ({
                ...blockRow,
                ...(blockRow.rows ? blockRow.rows[0] : {}),
              })),
            }),
          },
          confidence: row.color,
          label: row.primaryKey,
          nbFields: (row.fields || []).length,
        })),
      };
    }
  }
  return blocks;
}

export function getReviewTree(answer, blockArray) {
  const out = {};

  for (let i = 0; answer.fields && i < answer.fields.length; i++) {
    const field = answer.fields[i];
    if (!field.children) {
      // Default: look for answers at the root level
      let answers = field.answers;
      // Priority check: itemsApiAnswers, then itemsAnswers, then itemAnswers
      if (field?.itemsApiAnswers?.length) {
        answers = field.itemsApiAnswers;
      } else if (field?.itemsAnswers?.length) {
        answers = field.itemsAnswers;
      } else if (field.itemAnswers?.length) {
        answers = field.itemAnswers;
      }
      out[field.id] = {
        confidence: field.color,
        answers,
      };
    } else if (field.children) {
      out[field.id] = getReviewTree(field.children);
    }
  }

  for (let i = 0; answer.blocks && i < answer.blocks.length; i++) {
    const block = answer.blocks[i];
    out[block.id] = { $items: block.rows?.map((row) => getReviewTree(row)) || [] };
    out.blockArray = blockArray;
  }

  return out;
}

// Test if box is fully contained inside target
export function isInside(box, target) {
  return (
    target.x >= box.x &&
    target.y >= box.y &&
    target.x + target.width <= box.x + box.width &&
    target.y + target.height <= box.y + box.height
  );
}
// Move boxes down if it collides with something
export function reflow(boxes) {
  const numBoxes = boxes.length;
  if (numBoxes <= 1) return boxes;

  for (let i = 0; i < numBoxes; i++) {
    const box = boxes[i];

    // Skip boxes with height 0, as they do not contribute to reflow
    if (box.height === 0) continue;

    let hasCollision;
    do {
      hasCollision = false;

      // Loop in reverse to prioritize adjusting boxes that come after `box`
      for (let j = i + 1; j < numBoxes; j++) {
        const otherBox = boxes[j];

        // Skip if the `otherBox` is the same or has zero height
        if (otherBox.height === 0) continue;

        // Check for any kind of overlap
        if (isColliding(box, otherBox) || isInside(box, otherBox) || isInside(otherBox, box)) {
          // Increment `y` of the colliding box and set collision flag
          otherBox.y += 1;
          hasCollision = true;

          // Exit the inner loop to re-check all boxes for new positions
          break;
        }
      }
    } while (hasCollision); // Repeat if a collision was found
  }

  return boxes;
}

export function boxSize(box) {
  return box.width * box.height;
}

// Get the smallest possible block for a given box
// The box must fully fit inside the block
export function getSmallestBlock(node, blocksNodes, { duplicable } = {}) {
  if (!blocksNodes.length) return null;

  let block = null;
  let smallestSize = Infinity;
  const nodeBox = node.originalBox || node.box;
  const nodeSize = node.size;

  for (let b = 0, len = blocksNodes.length; b < len; b++) {
    const someBlock = blocksNodes[b];

    if (node === someBlock) continue;

    const blockSize = someBlock.size;
    if (nodeSize && nodeSize >= blockSize) continue;

    const blockBox = someBlock.originalBox || someBlock.box;
    if (!isInside(blockBox, nodeBox)) continue;

    const currentBlockSize = blockSize || boxSize(someBlock.box);
    if (currentBlockSize >= smallestSize) continue;

    if (duplicable && someBlock.duplicable !== duplicable) continue;

    block = someBlock;
    smallestSize = currentBlockSize;
  }

  return block;
}

export function getHighestBlock(node, blocksNodes) {
  let block = null;
  for (let b = 0; b < blocksNodes.length; b++) {
    const someBlock = blocksNodes[b];

    if (
      node !== someBlock &&
      (!node.size || node.size < someBlock.size) &&
      (!block || (block.size || boxSize(block.box)) < (someBlock.size || boxSize(someBlock.box))) &&
      isInside(someBlock.box, node.box)
    ) {
      block = someBlock;
    }
  }

  return block;
}

function xySort(a, b) {
  if (a.box.y < b.box.y) {
    return -1;
  } else if (a.box.y > b.box.y) {
    return 1;
  } else if (a.box.x < b.box.x) {
    return -1;
  } else if (a.box.x > b.box.x) {
    return 1;
  }

  return 0;
}

// Sort items by thier position index
function positionIndexSort(a, b) {
  return a.positionIndex - b.positionIndex;
}

// Recursively sort a form tree
function recursiveSort(node, compareFunc) {
  node.children.sort(compareFunc);

  if (node.children) {
    node.children.forEach((child) => child.children && recursiveSort(child, compareFunc));
  }
}

// This is where magic happens : convert a flat form structure to a tree with nested nodes
// The algorithm works as follow
// - Put all form fields in smallest possible block, if any
//   - Otherwise add it as root field node
// - Put all block in smallest possible block if any
//   - Other it is added as root block node
export function buildFormTree(form, { choicesOverrides } = {}) {
  const { blocks = [], fields = [], compareFields = [], gridSize } = form;

  const root = {
    nodeType: 'root',
    children: [],
    totalHeight: 0,
    hideRules: [],
    compareFields: compareFields
      .filter((compareFields) => !compareFields.blockScope)
      .map((compareFields) => compareFields.fields),
  };
  const blocksNodes = [];
  const rootFieldsNodes = [];

  // Build the tree : blocks node
  for (let b = 0, blocksLen = blocks.length; b < blocksLen; b++) {
    const block = blocks[b];
    const { box, hideIf } = block;

    const blockNode = {
      ...block,
      nodeType: BLOCK_NODE,
      // Make a copy of the box since we'll make it relative to parent block
      box: { ...box },
      // Keep a copy of the original box to do proper inclusion testing while building the tree
      originalBox: box,
      children: [],
      rowHeight: 0,
      order: block.order || 0,
      // Used to sort boxes by their position on the grid
      positionIndex: box.y * gridSize + box.x,
      // Compute area occupied by this block, we use this to determine smallest possible block
      size: box.width * box.height + (box.hasHeader ? box.width : 0),
    };
    blocksNodes.push(blockNode);

    // Add hideIf rule if defined
    if (hideIf?.enabled) {
      root.hideRules.push({
        id: block.id,
        ...hideIf,
        formula: hideIf.formula || [],
      });
    }
  }

  blocksNodes.sort(sortByPositionFactory(form.gridSize));

  // Create field nodes and fit in smallest possible block
  for (let f = 0, fieldsLen = fields.length; f < fieldsLen; f++) {
    const field = fields[f];
    const { box, hideIf, id } = field;

    const fieldNode = {
      nodeType: FIELD_NODE,
      ...field,
      // Used to sort boxes by their position on the grid
      positionIndex: box.y * gridSize + box.x,
      box: { ...box },
    };

    if (choicesOverrides && choicesOverrides[id]) {
      fieldNode.choices = choicesOverrides[id].replaceAll(
        fieldNode.injectSeparator || '&&',
        '\n',
      );
    }

    // Find the smallest possible block
    const block = getSmallestBlock(field, blocksNodes);

    if (block) {
      // Correct field offset to be relative to block offset (minus 1 for the header row)
      const blockBox = block.box;
      fieldNode.box.x = box.x - blockBox.x;
      fieldNode.box.y = box.y - blockBox.y - 1;

      block.children.push(fieldNode);

      // Updates row height if needed
      const newRowHeight = fieldNode.box.y + fieldNode.box.height;
      block.rowHeight = Math.max(block.rowHeight, newRowHeight);
      // Updates total height of the tree
      root.totalHeight = Math.max(root.totalHeight, newRowHeight);
    } else {
      // Field is not inside any block, add to root
      rootFieldsNodes.push(fieldNode);
    }

    // Add hideIf rule if defined
    if (hideIf?.enabled) {
      root.hideRules.push({
        ...hideIf,
        id,
      });
    }
  }

  // Fit blocks in smallest possible block
  for (let n = 0; n < blocksNodes.length; n++) {
    const node = blocksNodes[n];
    if (node.nodeType !== BLOCK_NODE) {
      continue;
    }

    // Find smallest block
    const block = getSmallestBlock(node, blocksNodes);

    if (block) {
      block.children.push(node);

      // Correct block offset to be relative to parent block (minus 1 for the header row)
      node.box.x = node.originalBox.x - block.originalBox.x;
      node.box.y = node.originalBox.y - block.originalBox.y - 1;

      // Updates parent block row height
      block.rowHeight = Math.max(block.rowHeight, node.box.y + node.box.height);
      // Updates total height of the tree
      root.totalHeight = Math.max(root.totalHeight, node.box.y + node.box.height);
    } else {
      root.children.push(node);
    }

    // Add hideIf rule if defined
    if (block?.hideIf?.enabled) {
      root.hideRules.push({
        ...block.hideIf,
        id: block.id,
      });
    }
  }

  // Sort stuff according to top-left position so order in the DOM matches x/y coords
  // This is usefull to keep native tabulation behavior on forms
  for (let n = 0; n < blocksNodes.length; n++) {
    blocksNodes[n].children.sort(xySort);
    // We've finished position testing, original box is no longer needed
    delete blocksNodes[n].originalBox;
  }

  for (let r = 0; r < root.hideRules.length; r++) {
    const hideRule = root.hideRules[r];
    const path = [
      ...getAncestorsChain(root, hideRule.id)
        .slice(0, -1)
        .map((node) => node.id),
      hideRule.id,
    ];
    hideRule.path = path;
  }

  // Add root field nodes to the tree, before blocks
  root.children.splice(0, 0, ...rootFieldsNodes);

  // Sort the whole tree structure by thier position (top left first)
  // When reflowing we want an ordered list of nodes so that boxes
  // at the bottom are moved before nodes at the top
  // Otherwise top boxes may be moved below bottom boxes
  recursiveSort(root, positionIndexSort);

  return root;
}

export function mapFormTree(tree, mapper) {
  return {
    ...tree,
    children: tree.children.map((node) => {
      const mappedNode = mapper(node);
      return node.children ? mapFormTree(mappedNode, mapper) : mappedNode;
    }),
  };
}

export function apiSelectTypologieTypeMapper(node) {
  const nodeCopy = { ...node };
  if (node.remoteOptionsEnabled && node.type === ADVANCEDSELECT) {
    nodeCopy.type = node.multi ? `api_multi${node.type}` : `api_${node.type}`;
  }
  return nodeCopy;
}

export function getAllCompareRules(form) {
  return [
    ...form.compareFields,
    ...form.blocks.reduce(
      (mergedCompareFields, block) =>
        block.compareFields
          ? mergedCompareFields.concat(
            block.compareFields.map((fields) => ({
              blockScope: block.id,
              fields,
            })),
          )
          : mergedCompareFields,
      [],
    ),
  ];
}

// Retrieve the ancestors chain of a node
export function getAncestorsChain(node, nodeId) {
  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];
    if (child.id === nodeId) {
      return [child];
    }

    if (child.children) {
      const chain = getAncestorsChain(child, nodeId);
      if (chain && chain.length) {
        return [child, ...chain];
      }
    }
  }

  return [];
}

// Retrieve a node by id in a deep tree structure
export function getNodeById(node, nodeId) {
  if (node.id === nodeId) {
    return node;
  }

  if (node.children) {
    for (let i = 0; i < node.children.length; i++) {
      const result = getNodeById(node.children[i], nodeId);

      if (result) {
        return result;
      }
    }
  }

  return null;
}

// Move a node and its children down
export function offsetNodeAndChildren(node) {
  node.box.y += 1;
  if (node.children) {
    for (let i = 0; i < node.children.length; i++) {
      offsetNodeAndChildren(node.children[i]);
    }
  }
  return node;
}

// Reflow childs of a single node
export function reflowNodeChilds(node, form) {
  node.children.sort(sortByPositionFactory(form.gridSize));

  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];
    if (node.box.y === child.box.y && node.box.hasHeader) {
      // If child is on header row, move it down
      child.box.y += 1;
    } else {
      // Else test blocks that comes before this one for collision
      // If this occurs, move block down
      for (let j = 0; j < i; j++) {
        const otherChild = node.children[j];
        if (isColliding(child.box, otherChild.box)) {
          offsetNodeAndChildren(child);
          // We wan't to do it only once per block
          break;
        }
      }
    }

    if (!isWithin(node.box, child.box)) {
      // If the child is no longer within the block, that means
      // it was pushed out of bounds, we need to increment height
      node.box = {
        ...node.box,
        height: node.box.height + 1,
      };
    }
  }

  return node;
}

// Transform relative positioning to absolute positioning
export function computeAbsolutePositions(node, offset = { x: 0, y: 0 }) {
  if (node.children) {
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i];
      child.box.x += offset.x;
      child.box.y += offset.y;
      if (child.children) {
        computeAbsolutePositions(child, {
          x: child.box.x,
          y: child.box.y + 1,
        });
      }
    }
  }
}

// Reflow the form layout when a header is added to a block
export function reflowAfterAddHeader(block, form) {
  // The tree structure is more convenient for reflowing
  const formTree = buildFormTree(form);
  // Set a box to the root for reflow constraints
  formTree.box = { x: 0, y: 0, width: form.gridSize, height: 1000 };
  // Transform relative => absolute positioning
  computeAbsolutePositions(formTree);

  // Find ancestors chain of updated block
  const ancestors = [formTree, ...getAncestorsChain(formTree, block.id)];

  // Reflow ancestors, starting from deepest chilmd
  for (let i = ancestors.length - 1; i > -1; i--) {
    reflowNodeChilds(ancestors[i], form);
  }

  // Once tree has been reflown, copy-back updated boxes to form
  for (let i = 0; i < form.fields.length; i++) {
    const treeNode = getNodeById(formTree, form.fields[i].id);
    form.fields[i] = {
      ...form.fields[i],
      box: {
        ...form.fields[i].box,
        ...treeNode.box,
      },
    };
  }

  // Same thing for blocks
  for (let i = 0; i < form.blocks.length; i++) {
    const treeNode = getNodeById(formTree, form.blocks[i].id);
    form.blocks[i] = {
      ...form.blocks[i],
      box: {
        ...form.blocks[i].box,
        ...treeNode.box,
      },
    };
  }

  return form;
}

export function lastItem(array) {
  return array[array.length - 1];
}

export function buildFormOutput(node, values, hiddenBoxes = []) {
  return buildNodeOutput(node, values, hiddenBoxes);
}

export function buildNodeOutput(node, values, hiddenPaths = []) {
  const out = {
    data: [],
    fields: [],
    blocks: [],
  };

  const { children } = node;
  const hiddenPathsSet = new Set(hiddenPaths);

  for (let i = 0, len = children.length; i < len; i++) {
    const child = children[i];
    const { id: childId, type: childType, nodeType, multi, remoteOptionsEnabled } = child;

    if (hiddenPathsSet.has(childId)) {
      continue;
    }

    if (nodeType === FIELD_NODE) {
      if (childType === GPSCOORDINATES) {
        // GPS Coordinates have a special output format that splits in two objects
        const coordValues = get(values, childId);
        out.fields.push(
          {
            type: 'coordinates_latitude',
            id: `${childId}_latitude`,
            value: coordValues?.latitude,
          },
          {
            type: 'coordinates_longitude',
            id: `${childId}_longitude`,
            value: coordValues?.longitude,
          },
        );
      } else {
        const nodeOutput = {
          type: childType,
          id: childId,
        };

        const fieldValue = get(values, childId);

        if (multi) {
          if (remoteOptionsEnabled) {
            // Select queryMode case
            nodeOutput.type = `api_multi${childType}`;
            nodeOutput.itemsApi = fieldValue?.map(selectRemoteValueToApi);
          } else {
            // Prefix mcq / select with "multi" when in multi mode
            nodeOutput.type = `multi${childType}`;
            nodeOutput.items = fieldValue;
          }
        } else if (childType === NUMBER) {
          const itemNumber = fieldValue || '';
          nodeOutput.value = itemNumber ? itemNumber.replace(',', '.') : null;
        } else {
          if (remoteOptionsEnabled) {
            nodeOutput.type = 'api_select';
            nodeOutput.itemApi = fieldValue ? selectRemoteValueToApi(fieldValue) : null;
          } else {
            nodeOutput.value = fieldValue;
          }
        }

        if (DATA_TYPE_LIST.includes(childType)) {
          out.data.push(nodeOutput);
        } else {
          out.fields.push(nodeOutput);
        }
      }
    } else if (nodeType === BLOCK_NODE) {
      const rows = values[childId]?.$items || [];

      // Process children data types
      for (let j = 0, childrenLen = child.children.length; j < childrenLen; j++) {
        const elem = child.children[j];
        if (DATA_TYPE_LIST.includes(elem.type)) {
          out.data.push({ type: elem.type, id: elem.id, value: values[elem.id] });
        }
      }

      const block = {
        type: 'block',
        id: childId,
        rows: rows.map((row, i) => {
          const rowPath = `${childId}.$items[${i}].`;
          const filteredHiddenPaths = hiddenPaths
            .filter((hiddenBox) => hiddenBox && hiddenBox.startsWith(rowPath))
            .map((hiddenBox) => hiddenBox.replace(rowPath, ''));
          return buildNodeOutput(child, row, filteredHiddenPaths);
        }),
      };
      out.blocks.push(block);
    }
  }
  return out;
}

export function clearHiddenValues(values, hiddenFields) {
  const cloned = clone(values);

  var existingHiddenFields = hiddenFields.filter((word) => word);

  existingHiddenFields
    .filter((word) => word)
    .forEach((hiddenPath) => {
      del(cloned, hiddenPath.replace(/(\[|\]\.)/g, '.'));
    });
  return cloned;
}

export function processDataColumns(columns) {
  if (!columns) {
    return {};
  }
  return columns
    .filter((column) => column.type?.startsWith('data_'))
    .reduce(
      (acc, { name, data }) => ({
        ...acc,
        [name]: data,
      }),
      {},
    );
}

export function processChoicesInjectionColumns(columns) {
  if (!columns) {
    return {};
  }

  return columns
    .filter((column) => column.type === MCQ || column.type === ADVANCEDSELECT)
    .reduce(
      (acc, { name, data }) => ({
        ...acc,
        [name]: data,
      }),
      {},
    );
}
